import React, { useEffect, useMemo, useState } from 'react';
import {
  ChartType,
  ChartTypeSelector,
  Granularity,
  GranularitySelector
} from '@/components/charts/chart-type-selector';
import { DisplayMode, DisplayModeSelector } from '@/components/charts/display-mode-selector';
import { generateColorMap } from '@/components/charts/graph-utils';
import { UTCDate } from '@date-fns/utc';
import { StackedSeriesChart } from '@/components/display/stacked-series-chart';
import { Switch } from '@headlessui/react';
import { clsx } from 'clsx';
import { TrpcGraphDataPoint } from 'shared/src/trpc-types/line-item-trpc-types';
import { useLineItemContext } from '@/features/line-items/line-items-context';
import { ChartDataPoint, ChartSeries } from '@/components/display/stacked-series-chart-utils';
import { isWithinRange } from 'shared/src/date-string-utils/date-utils';
import type { ISODateRange, ISODateString } from 'shared/src/date-string-utils/types';

type Props = {
  granularity: Granularity;
  setGranularity: (granularity: Granularity) => void;
  setColorMap?: (colorMap: Record<string, string>) => void;
  dateRange?: ISODateRange;
};

export function LineItemGraph({ granularity, setGranularity, setColorMap, dateRange }: Props) {
  const [displayMode, setDisplayMode] = useState<DisplayMode>('discrete');
  const [stacked, setStacked] = useState<boolean>(true);
  const [showPlanned, setShowPlanned] = useState<boolean>(true);
  const [showBars, setShowBars] = useState<boolean>(false);
  const [chartType, setChartType] = useState<ChartType>('spend');
  const { graphMetrics } = useLineItemContext();

  const graphData = useMemo<ChartSeries[]>(() => {
    if (graphMetrics.media_plans.length === 0) return [];
    return graphMetrics.media_plans.map(mp => ({
      id: mp.media_buy_id,
      name: mp.name,
      data: mp.data.filter(d => isBucketInRange(d.bucket, dateRange)).map(toChartDataPoint)
    }));
  }, [graphMetrics, dateRange]);

  const plannedData = useMemo<ChartDataPoint[]>(() => {
    return graphMetrics.planned
      .filter(d => isBucketInRange(d.bucket, dateRange))
      .map(toChartDataPoint);
  }, [graphMetrics, dateRange]);

  useEffect(() => {
    if (setColorMap) setColorMap(generateColorMap(graphData));
  }, [graphData, setColorMap]);

  return (
    <div className="flex min-h-[250px] grow flex-col">
      <div className="flex items-center">
        <ChartTypeSelector selected={chartType} setSelected={setChartType} />
        <GranularitySelector granularity={granularity} setGranularity={setGranularity} />
        <div className="flex-1" />
        <BooleanSelector label={'Show Planned'} value={showPlanned} setValue={setShowPlanned} />
        <BooleanSelector label={'Stacked'} value={stacked} setValue={setStacked} />
        <BooleanSelector label={'Show Bars'} value={showBars} setValue={setShowBars} />
        <DisplayModeSelector displayMode={displayMode} setDisplayMode={setDisplayMode} />
      </div>
      <div className="flex w-full min-w-0 flex-1 flex-col">
        <StackedSeriesChart
          graphData={graphData}
          plannedData={plannedData}
          chartType={chartType}
          displayMode={displayMode}
          stacked={stacked}
          showPlanned={showPlanned}
          showBars={showBars}
        />
      </div>
    </div>
  );
}

export function MediaPlanGraph({
  mediaPlanId,
  granularity,
  setGranularity,
  setColorMap
}: { mediaPlanId: string } & Props) {
  const [displayMode, setDisplayMode] = useState<DisplayMode>('discrete');
  const [showBars, setShowBars] = useState<boolean>(false);
  const [chartType, setChartType] = useState<ChartType>('spend');
  const { graphMetrics } = useLineItemContext();

  const graphData = useMemo<ChartSeries[]>(() => {
    // graphMetrics contains all media plans for the line item; filter to this media plan's series
    const filtered = graphMetrics.media_plans.filter(mp => mp.media_plan_id === mediaPlanId);
    if (filtered.length === 0) return [];
    return filtered.map(mp => ({
      id: mp.media_buy_id,
      name: mp.name,
      data: mp.data.map(toChartDataPoint)
    }));
  }, [graphMetrics, mediaPlanId]);

  useEffect(() => {
    if (setColorMap) setColorMap(generateColorMap(graphData));
  }, [graphData, setColorMap]);

  return (
    <div className="flex min-h-[250px] grow flex-col">
      <div className="flex items-center">
        <ChartTypeSelector selected={chartType} setSelected={setChartType} />
        <GranularitySelector granularity={granularity} setGranularity={setGranularity} />
        <div className="flex-1" />
        <BooleanSelector label={'Show Bars'} value={showBars} setValue={setShowBars} />
        <DisplayModeSelector displayMode={displayMode} setDisplayMode={setDisplayMode} />
      </div>
      <div className="flex w-full min-w-0 flex-1 flex-col">
        <StackedSeriesChart
          graphData={graphData}
          plannedData={[]}
          chartType={chartType}
          displayMode={displayMode}
          stacked={false}
          showPlanned={false}
          showBars={showBars}
        />
      </div>
    </div>
  );
}

export function BooleanSelector({
  label,
  value,
  setValue
}: {
  label: string;
  value: boolean;
  setValue: (value: boolean) => void;
}) {
  const handleChange = (checked: boolean) => {
    setValue(checked);
  };

  return (
    <div className="flex items-center">
      <div className="flex items-center mr-2">
        <span className="mr-2 text-sm">{label}</span>
        <Switch
          checked={value}
          onChange={handleChange}
          className={clsx(
            'group relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
            value ? 'bg-blue-600' : 'bg-gray-200'
          )}>
          <span className="group-data-checked:translate-x-6 h-4 w-4 translate-x-1 transform rounded-full bg-white transition-transform" />
        </Switch>
      </div>
    </div>
  );
}

function toChartDataPoint(d: TrpcGraphDataPoint): ChartDataPoint {
  return {
    date: new UTCDate(d.bucket),
    spend: d.spend,
    cumulative_spend: d.cumulative_spend,
    units: d.units,
    cumulative_units: d.cumulative_units
  };
}

function isBucketInRange(bucket: string, dateRange: ISODateRange | undefined): boolean {
  if (!dateRange) return true;
  const dateStr = bucket.substring(0, 10) as ISODateString;
  return isWithinRange(dateStr, dateRange);
}
